import type { ComponentType } from 'react';
import { createSlotComponent } from '@reflex/plugin-api/create-web-registration';
import { Bot, Building2, Container, Cpu, Server, User, Users } from 'lucide-react';
import type { ResourcePreviewProps } from '@reflex/plugin-api';
import type { IconComponent, ResolvedResourcePreview } from '@/lib/plugin-registry';
import { UserPreview as UserPreviewComponent } from '@/components/resource-preview/UserPreview';

interface BuiltInRegistration {
  kind: string;
  label: string;
  icon: IconComponent;
  component: ComponentType<ResourcePreviewProps>;
  detailHref?: string;
}

/**
 * Built-in resource-preview registrations for kinds that aren't owned by
 * any plugin (today: `agents`, `users`, `teams`, `organizations`).
 * Plugin-registered previews take precedence — see
 * {@link useResourcePreviewRegistration}. Add a new entry here only if
 * there is no plugin home for the kind; prefer extending a plugin's
 * `web.resourcePreviews` instead.
 */
const BUILT_IN_PREVIEWS: BuiltInRegistration[] = [
  {
    kind: 'agents',
    label: 'Agent',
    icon: Bot as IconComponent,
    detailHref: '/agents/:id',
    component: createSlotComponent(() =>
      import('@/components/resource-preview/AgentPreview').then((mod) => ({
        default: mod.AgentPreview,
      })),
    ) as ComponentType<ResourcePreviewProps>,
  },
  {
    kind: 'users',
    label: 'User',
    icon: User as IconComponent,
    // UserPreview is already statically imported by AgentOwnerAvatar, so a
    // dynamic import would not create a separate chunk — use a static import
    // to avoid the warning.
    component: UserPreviewComponent as ComponentType<ResourcePreviewProps>,
  },
  {
    kind: 'teams',
    label: 'Team',
    icon: Users as IconComponent,
    component: createSlotComponent(() =>
      import('@/components/resource-preview/TeamPreview').then((mod) => ({
        default: mod.TeamPreview,
      })),
    ) as ComponentType<ResourcePreviewProps>,
  },
  {
    kind: 'organizations',
    label: 'Organization',
    icon: Building2 as IconComponent,
    component: createSlotComponent(() =>
      import('@/components/resource-preview/OrganizationPreview').then((mod) => ({
        default: mod.OrganizationPreview,
      })),
    ) as ComponentType<ResourcePreviewProps>,
  },
  {
    kind: 'agent-types',
    label: 'Agent type',
    icon: Cpu as IconComponent,
    // No `detailHref`: the agent type catalogue lives in the docs corpus
    // (`/docs/concepts/agent-types`), not on an org-scoped app page, so the
    // preview card renders without an "open in" link.
    component: createSlotComponent(() =>
      import('@/components/resource-preview/AgentTypePreview').then((mod) => ({
        default: mod.AgentTypePreview,
      })),
    ) as ComponentType<ResourcePreviewProps>,
  },
  {
    kind: 'models',
    label: 'Model',
    icon: Bot as IconComponent,
    detailHref: '/org/agent-models',
    component: createSlotComponent(() =>
      import('@/components/resource-preview/ModelPreview').then((mod) => ({
        default: mod.ModelPreview,
      })),
    ) as ComponentType<ResourcePreviewProps>,
  },
  {
    kind: 'providers',
    label: 'Provider',
    icon: Server as IconComponent,
    detailHref: '/org/agent-models',
    component: createSlotComponent(() =>
      import('@/components/resource-preview/ProviderPreview').then((mod) => ({
        default: mod.ProviderPreview,
      })),
    ) as ComponentType<ResourcePreviewProps>,
  },
  {
    // Runloop devbox ids (`dbx_…`). No Reflex row backs them, so this is a
    // host built-in rather than a plugin registration; no `detailHref`
    // because the destination is the external Runloop dashboard, which the
    // preview links to itself.
    kind: 'sandboxes',
    label: 'Sandbox',
    icon: Container as IconComponent,
    component: createSlotComponent(() =>
      import('@/components/resource-preview/SandboxIdPreview').then((mod) => ({
        default: mod.SandboxIdPreview,
      })),
    ) as ComponentType<ResourcePreviewProps>,
  },
];

const BUILT_IN_MAP = new Map<string, BuiltInRegistration>(
  BUILT_IN_PREVIEWS.map((p) => [p.kind, p]),
);

/**
 * Look up a built-in preview registration by `kind`. Plugin-registered
 * previews always take precedence — callers should consult plugins first
 * and fall back to this registry only when no plugin owns the kind.
 */
export function getBuiltInResourcePreview(kind: string): ResolvedResourcePreview | undefined {
  const entry = BUILT_IN_MAP.get(kind);
  if (!entry) return undefined;
  return {
    kind: entry.kind,
    label: entry.label,
    icon: entry.icon,
    component: entry.component,
    ...(entry.detailHref ? { detailHref: entry.detailHref } : {}),
    pluginName: '__core__',
  };
}

/** Test-only helper exposing every built-in registration for assertions. */
export function _listBuiltInResourcePreviewKinds(): string[] {
  return BUILT_IN_PREVIEWS.map((p) => p.kind);
}

/**
 * Core (non-plugin) entity id prefixes mapped to the built-in preview `kind`
 * their ids should render with. Plugin-owned prefixes are resolved from
 * manifests instead (see `resolveEntityIdPreviews`); this only covers host
 * entities that already have a built-in preview above. Keep prefixes in sync
 * with `CORE_IDS` in `@reflex/shared`.
 */
const CORE_PREFIX_TO_PREVIEW_KIND: Record<string, string> = {
  agt: 'agents',
  usr: 'users',
  tem: 'teams',
  org: 'organizations',
  // Runloop devbox ids. External to Reflex (and their body is 19 chars, so
  // the chat scanner's strict 22-char pattern never matches them) — this
  // entry serves surfaces that badge an id they already hold, like Chief's
  // approval card.
  dbx: 'sandboxes',
};

/**
 * Resolve the built-in preview registration for a core id prefix, or
 * `undefined` when the prefix has no built-in preview. Plugin-registered
 * prefixes always take precedence — see `useEntityIdPreview`.
 */
export function getBuiltInEntityIdPreview(prefix: string): ResolvedResourcePreview | undefined {
  const kind = CORE_PREFIX_TO_PREVIEW_KIND[prefix];
  return kind ? getBuiltInResourcePreview(kind) : undefined;
}
